/**
 * Admin: clinic-level triage queue.
 *
 * Lists clients in the service group whose assignedTherapist is null
 * (i.e. signed up via a clinic-level invitation) and lets the admin
 * pick a therapist. Therapists are shown with their current load and
 * "accepting new clients" flag.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Avatar, Chip, Alert, CircularProgress, Divider,
  Button, MenuItem, TextField, Snackbar
} from '@mui/material';
import api from '../../../services/api';

export default function TriageQueueDialog({ groupId, token, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState({}); // clientId -> therapistId
  const [submittingId, setSubmittingId] = useState(null);
  const [toast, setToast] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/clinic-triage/service-groups/${groupId}/awaiting`, token);
      setData(res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load triage queue');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (groupId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId, token]);

  async function assign(clientId) {
    const therapistId = picks[clientId];
    if (!therapistId) return;
    setSubmittingId(clientId);
    try {
      await api.post(`/clinic-triage/clients/${clientId}/assign-therapist`, { therapistId }, token);
      setToast('Therapist assigned');
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Assignment failed');
    } finally {
      setSubmittingId(null);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (!data) return null;

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Clients here signed up via a clinic-level invitation and don&apos;t yet have a therapist assigned.
        Pick a therapist from the dropdown — load and "accepting new clients" status are shown.
      </Typography>

      {data.awaiting.length === 0 && (
        <Alert severity="success">No clients awaiting therapist assignment.</Alert>
      )}

      <Stack divider={<Divider />} spacing={0}>
        {data.awaiting.map((c) => (
          <Stack
            key={c._id}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ py: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">
                {`${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {c.email} · joined {c.joinedAt ? new Date(c.joinedAt).toLocaleString() : '—'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                select size="small" sx={{ minWidth: 280 }}
                label="Assign therapist"
                value={picks[c._id] || ''}
                onChange={(e) => setPicks((p) => ({ ...p, [c._id]: e.target.value }))}
              >
                {data.therapists.map((t) => (
                  <MenuItem key={t._id} value={t._id} disabled={!t.acceptingNewClients}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <Avatar src={t.profilePictureUrl || undefined} sx={{ width: 24, height: 24 }}>
                        {t.name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.currentLoad}/{t.capacity || '∞'} clients
                          {!t.acceptingNewClients ? ' · paused' : ''}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained" size="small"
                onClick={() => assign(c._id)}
                disabled={!picks[c._id] || submittingId === c._id}
              >
                {submittingId === c._id ? 'Assigning…' : 'Assign'}
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {data.therapists.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Therapists in this group</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {data.therapists.map((t) => (
              <Chip
                key={t._id}
                avatar={<Avatar src={t.profilePictureUrl || undefined}>{t.name?.charAt(0)}</Avatar>}
                label={`${t.name} · ${t.currentLoad}/${t.capacity || '∞'}`}
                color={t.acceptingNewClients ? 'default' : 'warning'}
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </Stack>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
